import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import {
  AlertCircle,
  ArrowRight,
  Award,
  Check,
  FileCog,
  Hash,
  Lock,
  RefreshCw,
  Shield,
  Sparkles,
  Zap,
} from "lucide-react";

import { translateAutoFlowStepError } from "@/lib/autoFlowStepError";
import { cn } from "@/lib/utils";
import type {
  AutoStep,
  UseOnboardingAutoFlowReturn,
} from "@/pages/onboarding/useOnboardingAutoFlow";

const PLAN_ICON: Record<AutoStep, JSX.Element> = {
  portal_user: <Lock aria-hidden />,
  company_info: <Shield aria-hidden />,
  cae_download: <Award aria-hidden />,
  idemisor_sync: <Hash aria-hidden />,
  proveedor_software: <FileCog aria-hidden />,
};

interface FormColProps {
  flow: UseOnboardingAutoFlowReturn;
  /** Wizard-level "continue" callback fired when user clicks the done CTA. */
  onContinue: () => void;
  /** Optional close handler — when phase is working, lets the user dismiss/abort. */
  onAbort?: () => void;
}

/**
 * Inline rendering of the auto-flow's action plan + step progress, designed
 * to live in the OnboardingShell's form column. Replaces the legacy
 * 880×auto fullscreen modal.
 */
export function AutoFlowFormColContent({
  flow,
  onContinue,
  onAbort,
}: FormColProps): JSX.Element {
  const { t } = useTranslation();
  const {
    steps: allSteps,
    phase,
    doneCount: allStepsDoneCount,
    totalCount: allStepsTotalCount,
    hasFailed,
    tiempoFmt,
    waitingForManualContinue,
    retry,
    manualContinue,
    confirmDeleteExistingUser,
    declineDeleteExistingUser,
  } = flow;

  // Scratch tenants skip proveedor_software entirely (initialized as
  // "skipped" — Fiscalo isn't yet a registrable proveedor for an
  // un-homologated RUT). When it's skipped we don't want it to render
  // at all in the plan, and the totalCount stat should drop to 4.
  const proveedorSkipped =
    allSteps.find((s) => s.step === "proveedor_software")?.status ===
    "skipped";
  const steps = proveedorSkipped
    ? allSteps.filter((s) => s.step !== "proveedor_software")
    : allSteps;
  const doneCount = proveedorSkipped
    ? allStepsDoneCount - 1
    : allStepsDoneCount;
  const totalCount = proveedorSkipped
    ? allStepsTotalCount - 1
    : allStepsTotalCount;

  // Step 1 (portal_user / DGI auth) is the only one that actually
  // requires the user to be present, but the user now waits for the whole
  // chain: advancing early let them reach the certificate step before the
  // CAEs and the proveedor registration existed.
  // When the failure happens on step 1 the user can recover by creating
  // the e-Factura user manually. For any later step, the recovery is a
  // handoff to the Fiscalo team — both copy and the abort CTA need to
  // reflect that distinction.
  const portalUserFailed =
    steps.find((s) => s.step === "portal_user")?.status === "failed";

  if (phase === "done") {
    // proveedorSkipped is computed up-top from the unfiltered steps so
    // we can swap the done lead copy when Fiscalo isn't yet a
    // registrable proveedor for this tenant (scratch case). The user
    // still needs the manual DGI homologation + proveedor registration,
    // and the copy makes that explicit.
    return (
      <>
        <div className="fx-onb-done-hero">
          <span className="fx-onb-done-ring" aria-hidden>
            <Check />
          </span>
          <span className="fx-onb-modal-eyebrow">
            {t("onboarding.autoFlow.doneEyebrow", "Configuración completa")}
          </span>
          <h1 className="fx-onb-h1" style={{ textAlign: "center" }}>
            {t("onboarding.autoFlow.doneH2Lead", "Listo. DGI está")}{" "}
            <span className="ital">
              {t("onboarding.autoFlow.doneH2Accent", "configurado.")}
            </span>
          </h1>
          <p className="fx-onb-lead" style={{ textAlign: "center" }}>
            {proveedorSkipped
              ? t(
                  "onboarding.autoFlow.doneLeadScratch",
                  "Sincronizamos tu usuario e-Factura, los CAEs y los últimos números utilizados. Cuando DGI te habilite como emisor electrónico, registrás a Fiscalo como tu proveedor desde el portal (te recordamos cómo desde el dashboard).",
                )
              : t(
                  "onboarding.autoFlow.doneLead",
                  "Sincronizamos tu usuario e-Factura, los CAEs y los últimos números utilizados, y registramos a Fiscalo como tu proveedor de factura electrónica. Falta subir tu certificado digital para empezar a emitir.",
                )}
          </p>
        </div>

        <div className="fx-onb-done-stats">
          <div className="fx-onb-done-stat accent">
            <div className="k">
              {t("onboarding.autoFlow.statTime", "Tiempo total")}
            </div>
            <div className="v">{tiempoFmt}</div>
          </div>
          <div className="fx-onb-done-stat">
            <div className="k">
              {t("onboarding.autoFlow.statSteps", "Pasos")}
            </div>
            <div className="v">{totalCount}</div>
          </div>
          <div className="fx-onb-done-stat">
            <div className="k">
              {t("onboarding.autoFlow.statStatus", "Estado")}
            </div>
            <div className="v">OK</div>
          </div>
        </div>

        <div className="fx-onb-actions" style={{ marginTop: 12 }}>
          <div className="side" />
          <div className="side end">
            <button
              type="button"
              className="fx-onb-btn fx-onb-btn-accent"
              onClick={onContinue}
            >
              {t("onboarding.autoFlow.doneCta", "Subir certificado")}
              <ArrowRight aria-hidden />
            </button>
          </div>
        </div>
      </>
    );
  }

  // working / failed phase
  return (
    <>
      {hasFailed ? (
        <span className="fx-onb-modal-eyebrow">
          {t("onboarding.autoFlow.failedEyebrow", "Algo falló")}
        </span>
      ) : null}
      <h1 className="fx-onb-h1">
        {hasFailed ? (
          <>
            {t("onboarding.autoFlow.failedH2Lead", "Encontramos un")}{" "}
            <span className="ital">
              {t("onboarding.autoFlow.failedH2Accent", "problema.")}
            </span>
          </>
        ) : (
          <>
            {t("onboarding.autoFlow.workingH2Lead", "Fiscalo está")}{" "}
            <span className="ital">
              {t("onboarding.autoFlow.workingH2Accent", "configurando.")}
            </span>
          </>
        )}
      </h1>
      <p className="fx-onb-lead">
        {hasFailed
          ? portalUserFailed
            ? t(
                "onboarding.autoFlow.failedLeadPortalUser",
                "Te notificamos y nuestro equipo está revisando. Mientras tanto podés crear el usuario manualmente en DGI — toma unos minutos más.",
              )
            : t(
                "onboarding.autoFlow.failedLeadHandoff",
                "Te notificamos y nuestro equipo está revisando. Lo termina Fiscalo por vos — podés seguir con el resto del onboarding mientras tanto.",
              )
          : t(
              "onboarding.autoFlow.workingLead",
              "Quedate por acá — esto toma unos minutos. Si cerrás esta ventana, seguimos trabajando igual.",
            )}
      </p>

      <div className="fx-onb-plan">
        {steps.map((s) => {
          const cls =
            s.status === "success" || s.status === "skipped"
              ? "done"
              : s.status === "running"
                ? "running"
                : "";
          return (
            <div key={s.step} className={cn("fx-onb-plan-item", cls)}>
              <span className="tile" aria-hidden>
                {s.status === "success" || s.status === "skipped" ? (
                  <Check />
                ) : s.status === "running" ? (
                  <RefreshCw />
                ) : s.status === "failed" ? (
                  <AlertCircle />
                ) : (
                  PLAN_ICON[s.step]
                )}
              </span>
              <span className="body">
                <span className="label">{s.label}</span>
                {s.status === "running" && (
                  <span className="sub">{s.description}</span>
                )}
                {s.status === "failed" && (
                  <span
                    className="sub"
                    style={{
                      color: "var(--fx-warn)",
                      textTransform: "none",
                      letterSpacing: "0.01em",
                      fontFamily: "inherit",
                      fontSize: 12.5,
                      fontWeight: 500,
                    }}
                  >
                    {translateAutoFlowStepError(s.error, t)}
                  </span>
                )}
              </span>
              {s.status === "failed" ? (
                <span className="when-stack">
                  <span className="when failed">
                    {t("onboarding.autoFlow.statusFailed", "Falló")}
                  </span>
                  <button
                    type="button"
                    className="fx-onb-plan-retry"
                    onClick={() => void retry(s.step)}
                  >
                    {t("onboarding.autoFlow.retry", "Reintentar")}
                  </button>
                </span>
              ) : s.awaiting === "delete_existing_user" ? (
                <span
                  className="when"
                  style={{ color: "var(--fx-warn)", fontWeight: 600 }}
                >
                  {t(
                    "onboarding.autoFlow.statusActionRequired",
                    "Acción requerida",
                  )}
                </span>
              ) : (
                <span className="when">
                  {s.status === "success" || s.status === "skipped"
                    ? t("onboarding.autoFlow.statusDone", "Listo")
                    : s.status === "running"
                      ? t("onboarding.autoFlow.statusRunning", "Ahora")
                      : t("onboarding.autoFlow.statusPending", "Pendiente")}
                </span>
              )}
            </div>
          );
        })}
        <div className="fx-onb-plan-foot">
          {hasFailed
            ? t(
                "onboarding.autoFlow.failureNotice",
                "Detectamos un error · nuestro equipo fue notificado",
              )
            : (() => {
                const runningStep = steps.find((s) => s.status === "running")?.step;
                if (runningStep === "cae_download") {
                  return t(
                    "onboarding.autoFlow.workingFootCae",
                    "Tiempo restante · ~ 1–2 minutos",
                  );
                }
                if (runningStep === "proveedor_software") {
                  return t(
                    "onboarding.autoFlow.workingFootProveedor",
                    "Tiempo restante · ~ 3–10 minutos · DGI nos envía un código por email",
                  );
                }
                return t(
                  "onboarding.autoFlow.workingFoot",
                  "Tiempo restante · ~ pocos minutos",
                );
              })()}
        </div>
      </div>

      {waitingForManualContinue && (
        <div
          className="fx-onb-plan-foot"
          style={{
            borderRadius: 10,
            background: "rgba(45,97,73,0.08)",
            display: "inline-flex",
            alignItems: "center",
            gap: 12,
            padding: "8px 12px",
            justifyContent: "space-between",
          }}
        >
          <span>[Debug] Continuar a: {waitingForManualContinue}</span>
          <button
            type="button"
            className="fx-onb-btn fx-onb-btn-secondary"
            onClick={() => void manualContinue()}
            style={{ padding: "4px 10px", fontSize: 12 }}
          >
            Continuar
          </button>
        </div>
      )}

      <div
        className="fx-onb-actions"
        style={{ marginTop: 12, justifyContent: "space-between" }}
      >
        {hasFailed ? (
          <>
            <div className="side" />
            <div className="side end">
              {onAbort ? (
                <button
                  type="button"
                  className="fx-onb-btn fx-onb-btn-accent"
                  onClick={onAbort}
                >
                  {portalUserFailed
                    ? t(
                        "onboarding.autoFlow.switchToManual",
                        "Pasar a manual",
                      )
                    : t(
                        "onboarding.autoFlow.switchToHandoff",
                        "Que Fiscalo lo termine",
                      )}
                  <ArrowRight aria-hidden />
                </button>
              ) : null}
            </div>
          </>
        ) : (
          <>
            <div className="side">
              {onAbort ? (
                <button
                  type="button"
                  className="fx-onb-btn fx-onb-btn-ghost"
                  onClick={onAbort}
                >
                  {t("onboarding.autoFlow.cancel", "Cancelar")}
                </button>
              ) : null}
            </div>
            {/* No "continue in background" CTA. The only way out of this
                screen is the flow finishing (the phase === "done" branch
                offers "Subir certificado") or cancelling. Letting someone
                advance while steps 2-6 were still running meant they could
                reach the certificate step with no CAEs and no proveedor
                registration, and only find out by email. */}
            <div className="side end" />
          </>
        )}
      </div>

      {steps.some((s) => s.awaiting === "delete_existing_user") &&
        createPortal(
          <div
            className="fx-onb-modal-backdrop"
            role="dialog"
            aria-modal="true"
            aria-labelledby="fx-onb-regenerate-title"
          >
            <div className="fx-onb-modal">
              <div className="fx-onb-modal-form fx-onb-regenerate-form">
                <span className="fx-onb-modal-eyebrow fx-onb-regenerate-eyebrow">
                  <span className="dot" aria-hidden />
                  {t(
                    "onboarding.autoFlow.regenerateModalEyebrow",
                    "Acción requerida",
                  )}
                </span>
                <h2
                  id="fx-onb-regenerate-title"
                  className="fx-onb-modal-h2"
                >
                  {t(
                    "onboarding.autoFlow.regenerateModalTitleLead",
                    "Ya hay un usuario e-Factura en",
                  )}{" "}
                  <span className="ital">
                    {t(
                      "onboarding.autoFlow.regenerateModalTitleAccent",
                      "DGI.",
                    )}
                  </span>
                </h2>
                <p className="fx-onb-modal-lead">
                  {t(
                    "onboarding.autoFlow.regenerateModalLead",
                    "Detectamos un usuario antiguo registrado bajo este RUT. Para que Fiscalo pueda operar como tu software de facturación necesitamos regenerarlo.",
                  )}
                </p>
                <ul className="fx-onb-regenerate-plan">
                  <li>
                    <span className="ico" aria-hidden>
                      <RefreshCw />
                    </span>
                    <span>
                      <strong>
                        {t(
                          "onboarding.autoFlow.regenerateModalPoint1Title",
                          "Damos de baja el usuario anterior",
                        )}
                      </strong>
                      <span className="sub">
                        {t(
                          "onboarding.autoFlow.regenerateModalPoint1Sub",
                          "DGI no permite más de un usuario activo por RUT.",
                        )}
                      </span>
                    </span>
                  </li>
                  <li>
                    <span className="ico" aria-hidden>
                      <Lock />
                    </span>
                    <span>
                      <strong>
                        {t(
                          "onboarding.autoFlow.regenerateModalPoint2Title",
                          "Creamos uno nuevo a tu nombre",
                        )}
                      </strong>
                      <span className="sub">
                        {t(
                          "onboarding.autoFlow.regenerateModalPoint2Sub",
                          "Con los permisos justos para emitir y consultar.",
                        )}
                      </span>
                    </span>
                  </li>
                  <li>
                    <span className="ico" aria-hidden>
                      <Sparkles />
                    </span>
                    <span>
                      <strong>
                        {t(
                          "onboarding.autoFlow.regenerateModalPoint3Title",
                          "Quedamos vinculados como tu proveedor",
                        )}
                      </strong>
                      <span className="sub">
                        {t(
                          "onboarding.autoFlow.regenerateModalPoint3Sub",
                          "Sin reescribir tu historial — tus comprobantes anteriores siguen intactos.",
                        )}
                      </span>
                    </span>
                  </li>
                </ul>
                <div
                  className="fx-onb-actions split"
                  style={{ marginTop: 8 }}
                >
                  <div className="side">
                    <button
                      type="button"
                      className="fx-onb-btn fx-onb-btn-secondary"
                      onClick={() => void declineDeleteExistingUser()}
                    >
                      {t(
                        "onboarding.autoFlow.declineDeleteExistingUser",
                        "Cancelar",
                      )}
                    </button>
                  </div>
                  <div className="side end">
                    <button
                      type="button"
                      className="fx-onb-btn fx-onb-btn-accent"
                      onClick={() => void confirmDeleteExistingUser()}
                    >
                      {t(
                        "onboarding.autoFlow.regenerateModalConfirm",
                        "Regenerar usuario",
                      )}
                      <ArrowRight aria-hidden />
                    </button>
                  </div>
                </div>
              </div>

              <div className="fx-onb-modal-aside fx-onb-regenerate-aside">
                <span className="fx-onb-modal-eyebrow">
                  <span className="dot" aria-hidden />
                  {t(
                    "onboarding.autoFlow.regenerateModalAsideEyebrow",
                    "Por qué te lo preguntamos",
                  )}
                </span>
                <p className="fx-onb-modal-h3">
                  {t(
                    "onboarding.autoFlow.regenerateModalAsideH3Lead",
                    "Es un cambio que necesita",
                  )}{" "}
                  <span className="ital">
                    {t(
                      "onboarding.autoFlow.regenerateModalAsideH3Accent",
                      "tu permiso.",
                    )}
                  </span>
                </p>
                <p className="sub">
                  {t(
                    "onboarding.autoFlow.regenerateModalAsideSub1",
                    "Reemplazar el usuario implica desactivar el acceso anterior. Lo hacemos automáticamente desde la sesión que ya autenticaste con DGI.",
                  )}
                </p>
                <div className="fx-onb-regenerate-callout">
                  <span className="ico" aria-hidden>
                    <Shield />
                  </span>
                  <span>
                    <strong>
                      {t(
                        "onboarding.autoFlow.regenerateModalCalloutTitle",
                        "Tu información sigue segura",
                      )}
                    </strong>
                    <span>
                      {t(
                        "onboarding.autoFlow.regenerateModalCalloutSub",
                        "El cambio aplica únicamente al usuario técnico de e-Factura. Tus CAEs, comprobantes y datos fiscales no se tocan.",
                      )}
                    </span>
                  </span>
                </div>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}

interface BrandColProps {
  flow: UseOnboardingAutoFlowReturn;
}

/**
 * Inline rendering of the live activity ticker for the brand column.
 * In `done` phase, becomes the "Lo que dejamos hecho" audit list.
 */
export function AutoFlowBrandColContent({ flow }: BrandColProps): JSX.Element {
  const { t } = useTranslation();
  const { phase, activityTicker } = flow;

  return (
    <div className="fx-onb-brand-col">
      <div>
        <span className="fx-onb-brand-eyebrow">
          <span className="dot" />
          {phase === "done"
            ? t(
                "onboarding.autoFlow.asideDoneEyebrow",
                "Lo que dejamos hecho",
              )
            : t(
                "onboarding.autoFlow.asideWorkingEyebrow2",
                "Modo automático",
              )}
        </span>
        <p className="fx-onb-brand-quote">
          {phase === "done" ? (
            <>
              {t("onboarding.autoFlow.asideDoneH3Lead", "Fiscalo trabajó")}{" "}
              <span className="accent">
                {t("onboarding.autoFlow.asideDoneH3Accent", "por vos.")}
              </span>
            </>
          ) : (
            <>
              {t(
                "onboarding.autoFlow.asideWorkingH3Lead2",
                "Configurás una vez,",
              )}{" "}
              <span className="accent">
                {t(
                  "onboarding.autoFlow.asideWorkingH3Accent2",
                  "emitís para siempre.",
                )}
              </span>
            </>
          )}
        </p>
      </div>

      {phase === "done" ? (
        <ul className="fx-onb-ticker">
          {activityTicker.length === 0 ? (
            <li>
              <span className="ico" aria-hidden>
                <RefreshCw />
              </span>
              <span>{t("onboarding.autoFlow.tickerInit", "Iniciando…")}</span>
              <span className="ts">--:--</span>
            </li>
          ) : (
            activityTicker.map((e, i) => (
              <li key={i} className={cn(e.live && "live")}>
                <span className="ico" aria-hidden>
                  {e.live ? <RefreshCw /> : <Check />}
                </span>
                <span>{e.label}</span>
                <span className="ts">{e.ts}</span>
              </li>
            ))
          )}
        </ul>
      ) : (
        <ul className="fx-onb-brand-benefits">
          <li>
            <span className="ico" aria-hidden>
              <Shield />
            </span>
            <span>
              <strong>
                {t(
                  "onboarding.autoFlow.asideBenefit1Title",
                  "Sin abrir DGI",
                )}
              </strong>
              <span className="sub">
                {t(
                  "onboarding.autoFlow.asideBenefit1Sub",
                  "Operamos desde la sesión que ya autenticaste.",
                )}
              </span>
            </span>
          </li>
          <li>
            <span className="ico" aria-hidden>
              <Lock />
            </span>
            <span>
              <strong>
                {t(
                  "onboarding.autoFlow.asideBenefit2Title",
                  "Sesión segura",
                )}
              </strong>
              <span className="sub">
                {t(
                  "onboarding.autoFlow.asideBenefit2Sub",
                  "No almacenamos tu contraseña — solo el token temporal.",
                )}
              </span>
            </span>
          </li>
          <li>
            <span className="ico" aria-hidden>
              <Zap />
            </span>
            <span>
              <strong>
                {t(
                  "onboarding.autoFlow.asideBenefit3Title",
                  "Una sola vez",
                )}
              </strong>
              <span className="sub">
                {t(
                  "onboarding.autoFlow.asideBenefit3Sub",
                  "Configurás hoy y emitís durante años sin volver a tocar DGI.",
                )}
              </span>
            </span>
          </li>
        </ul>
      )}

      {phase === "done" ? (
        <div className="fx-onb-aside-foot">
          <span className="dot" aria-hidden />
          {t(
            "onboarding.autoFlow.asideDoneFoot",
            "Auditoría guardada · Documentos / DGI",
          )}
        </div>
      ) : null}
    </div>
  );
}
